// packages/shared/src/compare-cell.ts
//
// Order comparison for one cell against one filter term — the `>=` `<=` `>` `<`
// half of the filter language.
//
// Separate from `column-filter.ts` because the answer depends on the COLUMN
// TYPE in a way no other token does: `'10' >= '9'` is false as text and true as
// a number, and both readings are right for some column. `column-filter.ts`
// owns the grammar; this file owns what the grammar's comparison MEANS.
//
// `compareKey` is type-aware: `number` compares numerically, `date`/`datetime`
// compare as normalised ISO text, and every other type falls back to
// lowercased text, which is what the grammar did before comparisons existed.
import { resolveDateTerm } from './relative-date.js';
/** `YYYY-MM-DD`, and the date half of a naive datetime. */
const DATE_ONLY = /^(\d{4}-\d{2}-\d{2})$/;
const NAIVE = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/;
const ZONED = /(?:Z|[+-]\d{2}:?\d{2})$/i;
/**
 * A cell or a bound as something two of its kind can be ordered by, or null
 * when it cannot be read as one.
 *
 * `date` and `datetime` come back as normalised ISO TEXT rather than an epoch
 * number, because ISO text sorts correctly as text and stays readable in a
 * failing test. A value carrying a zone is converted to **UTC** first — not to
 * the reader's local clock — so that this agrees with SQLite's `date()` /
 * `datetime()`, which is what the SQL pushdown uses. A local rule could not be
 * reproduced in SQL and the two answers would drift.
 */
export function compareKey(value, type) {
    if (value == null)
        return null;
    if (type === 'number') {
        const n = typeof value === 'number' ? value : Number(String(value).trim());
        return Number.isFinite(n) ? n : null;
    }
    const s = String(value).trim();
    if (s === '')
        return null;
    if (type === 'date')
        return isoOf(s)?.slice(0, 10) ?? null;
    if (type === 'datetime')
        return isoOf(s);
    return s.toLowerCase();
}
/**
 * `YYYY-MM-DDTHH:mm:ss` in UTC, or the date alone when that is all there was.
 *
 * A date-only value is NOT put through `new Date(s)`: that parses as midnight
 * UTC, and west of Greenwich the round trip comes back a day early. The parts
 * are used as written — the classic date bug, avoided the same way
 * `renderer/util/local-datetime.ts` avoids it.
 */
function isoOf(s) {
    if (DATE_ONLY.test(s))
        return s;
    const naive = NAIVE.exec(s);
    if (naive && !ZONED.test(s))
        return `${naive[1]}T${naive[2]}:${naive[3]}:${naive[4] ?? '00'}`;
    const d = new Date(s);
    if (Number.isNaN(d.getTime()))
        return null;
    return d.toISOString().slice(0, 19);
}
/**
 * Would `compareKey` be able to read `bound` as a value of `type`? Used by
 * `filter-sql.ts` to decide whether a comparison bound has a meaningful SQL
 * form at all — an unparseable bound must fail every comparison, the same as
 * `satisfiesCmp` does below, rather than being bound in as raw text and
 * compared lexicographically (which can return every row instead of none,
 * depending on which side of the alphabet the malformed text happens to
 * sort).
 *
 * Mirrors `satisfiesCmp`'s `dateOnlyBound` special case: a DATE-ONLY bound
 * against a `datetime` column is validated as a `date`, matching what it is
 * actually compared against.
 */
export function isValidBound(bound, type) {
    const dateOnlyBound = type === 'datetime' && DATE_ONLY.test(bound);
    const keyType = dateOnlyBound ? 'date' : type;
    return compareKey(bound, keyType) !== null;
}
/**
 * Does `value` stand in relation `cmp` to `term`?
 *
 * False for a cell with no content, and for one whose key cannot be read: a
 * comparison against nothing is not an order relation, and the matcher's rule
 * is that an empty cell fails a positive test (and therefore passes its
 * negation, which the caller applies).
 *
 * On a `datetime` column a DATE-ONLY bound compares only the date part, so
 * `<=2026-08-01` covers all of that day rather than cutting at midnight — which
 * is what someone who typed a date meant.
 *
 * On a `date` / `datetime` column the bound may also be a RELATIVE term
 * (`-3m`, `ytd`, …), resolved against `now` — see `relative-date.ts`. Any
 * other column type leaves the term alone: `-3m` there is a literal value
 * someone might really hold in a cell.
 */
export function satisfiesCmp(value, term, cmp, type, now) {
    let bound = term.trim();
    if (bound === '')
        return false;
    if (type === 'date' || type === 'datetime')
        bound = resolveDateTerm(bound, now) ?? bound;
    const dateOnlyBound = type === 'datetime' && DATE_ONLY.test(bound);
    const keyType = dateOnlyBound ? 'date' : type;
    const a = compareKey(value, keyType);
    const b = compareKey(bound, keyType);
    if (a === null || b === null)
        return false;
    if (typeof a !== typeof b)
        return false;
    return order(a, b, cmp);
}
/**
 * The four operators, over any two already-comparable values.
 *
 * Generic because TypeScript refuses `<` between two `string | number` unions,
 * even once the caller has proved both sides have the same runtime type. The
 * caller's `as string` is a compile-time pin, not a conversion — two numbers
 * still compare as numbers at runtime.
 */
function order(a, b, cmp) {
    if (cmp === '>=')
        return a >= b;
    if (cmp === '<=')
        return a <= b;
    if (cmp === '>')
        return a > b;
    return a < b;
}
//# sourceMappingURL=compare-cell.js.map